import { DEFAULT_TIP_PERCENTAGES } from '@order-up/models';
import { z } from 'zod';

// ─── Enums ──────────────────────────────────────────────────────────────────

export const KitchenSendTriggerSchema = z.enum([
  'manual',
  'onPaymentStart',
  'onPaymentComplete',
  'promptOnPay',
]);

export const OrderCompletionModeSchema = z.enum([
  'closeOnPayment',
  'closeOnServe',
  'closeOnPaymentOrServe',
]);

export const UpdateScheduleModeSchema = z.enum([
  'outsideOpenHours',
  'fixedTime',
]);

// ─── Sub-config Schemas ─────────────────────────────────────────────────────

export const PosKitchenConfigSchema = z.object({
  enabled: z.boolean().default(true),
  sendTrigger: KitchenSendTriggerSchema.default('manual'),
  showSendButtonAllOrderTypes: z.boolean().default(false),
  allowPartialSend: z.boolean().default(true),
  autoServeOnPayment: z.boolean().default(false),
});

export const PosOrderCompletionConfigSchema = z.object({
  mode: OrderCompletionModeSchema.default('closeOnPaymentOrServe'),
  autoStartNewOrder: z.boolean().default(false),
  newOrderDelayMs: z.number().int().min(0).max(10000).default(0),
});

export const PosPaymentConfigSchema = z.object({
  allowTips: z.boolean().default(true),
  tipPercentages: z.array(z.number().min(0).max(1)).default([...DEFAULT_TIP_PERCENTAGES]),
  autoPrintCardReceipts: z.boolean().default(false),
  autoPrintCashReceipts: z.boolean().default(true),
  printCustomerReceipt: z.boolean().default(true),
  printMerchantReceipt: z.boolean().default(true),
  openCashDrawerOnCash: z.boolean().default(true),
  openCashDrawerOnCard: z.boolean().default(false),
  cashDrawerPulseMs: z.number().int().min(100).max(500).default(100),
  /** Over/short in CENTS a close may reach before needing manager approval. 0 = always approve. */
  varianceApprovalThresholdCents: z.number().int().min(0).max(100_000).default(500),
  /** Every shift opens on a counted drawer (server refuses an open without a count sheet). */
  requireOpeningCount: z.boolean().default(false),
  /** No cash tender until a shift is open on the till's drawer (enforced by the till). */
  requireCashShift: z.boolean().default(false),
});

export const PosUpdateConfigSchema = z.object({
  scheduleMode: UpdateScheduleModeSchema.default('outsideOpenHours'),
  fixedTime: z
    .string()
    .regex(/^([01]?\d|2[0-3]):[0-5]\d$/, 'fixedTime must be HH:mm (24h)')
    .optional(),
  fixedWindowMinutes: z.number().int().min(15).max(1440).default(120),
  applyWhenIdle: z.boolean().default(true),
  idleThresholdMinutes: z.number().int().min(1).max(120).default(5),
  // Restart-storm guards (2026-07-11 incident): a logged-out terminal only
  // counts as idle after a continuous sessionless floor, and idle-lull
  // applies are rate-limited outside the install window.
  sessionlessIdleFloorMinutes: z.number().int().min(5).max(1440).default(60),
  minAutoRestartGapMinutes: z.number().int().min(0).max(1440).default(240),
});

// ─── Shift Review ───────────────────────────────────────────────────────────

export const ShiftReviewSectionKeySchema = z.enum([
  'employeeAccount',
  'tipsAndFeesEarned',
  'myGuestReport',
  'creditTipAudit',
  'tipSharing',
  'delivery',
  'salesAndTaxesSummary',
  'cashCreditPerSalesCategory',
  'revenueCenters',
  'totalVoids',
  'totalRemovals',
  'totalDiscounts',
  'totalPayments',
  'creditCardBreakdown',
  'otherBreakdown',
  'employeeSignature',
  'payOuts',
]);

export const ShiftReviewSectionSchema = z.object({
  key: ShiftReviewSectionKeySchema,
  visible: z.boolean(),
});

/**
 * The X tape an employee clears before clocking out.
 *
 * `sections` may be PARTIAL and may be empty — `resolveShiftReviewSections`
 * (models) appends anything missing, so a restaurant that saved its report
 * before a new section shipped still sees that section. Validating it as
 * exhaustive here would refuse every config saved by an older build.
 */
export const PosShiftReviewConfigSchema = z.object({
  requiredBeforeClockOut: z.boolean().default(false),
  sections: z.array(ShiftReviewSectionSchema).default([]),
});

// ─── Top-Level POS Config Schema ────────────────────────────────────────────

export const PosConfigSchema = z.object({
  kitchen: PosKitchenConfigSchema.default({
    enabled: true,
    sendTrigger: 'manual',
    showSendButtonAllOrderTypes: false,
    allowPartialSend: true,
    autoServeOnPayment: false,
  }),
  orderCompletion: PosOrderCompletionConfigSchema.default({
    mode: 'closeOnPaymentOrServe',
    autoStartNewOrder: false,
    newOrderDelayMs: 0,
  }),
  payment: PosPaymentConfigSchema.default({
    allowTips: true,
    tipPercentages: [...DEFAULT_TIP_PERCENTAGES],
    autoPrintCardReceipts: false,
    autoPrintCashReceipts: true,
    printCustomerReceipt: true,
    printMerchantReceipt: true,
    openCashDrawerOnCash: true,
    openCashDrawerOnCard: false,
    cashDrawerPulseMs: 100,
    varianceApprovalThresholdCents: 500,
    requireOpeningCount: false,
    requireCashShift: false,
  }),
  update: PosUpdateConfigSchema.default({
    scheduleMode: 'outsideOpenHours',
    fixedWindowMinutes: 120,
    applyWhenIdle: true,
    idleThresholdMinutes: 5,
    sessionlessIdleFloorMinutes: 60,
    minAutoRestartGapMinutes: 240,
  }),
  /**
   * Declared here, not merely on IPosConfig, because a structured posConfig
   * save is parsed through this schema — a field the schema does not know is
   * silently STRIPPED, so the operator's setting would appear to save and then
   * be gone on the next read.
   */
  shiftReview: PosShiftReviewConfigSchema.default({
    requiredBeforeClockOut: false,
    sections: [],
  }),
  /**
   * Per-restaurant printing behaviour for the till. Declared here for the
   * reason stated above `shiftReview`: a structured posConfig save is parsed
   * through this schema, so a field the schema does not know is silently
   * STRIPPED — the operator's choice would appear to save and be gone on the
   * next read. `apps/pos` has read these two keys all along.
   *
   * `.optional()` with NO `.default()`: absent means "this restaurant has no
   * opinion", and the till applies its own safe fallback (the SDK driver, and
   * drawer-open-when-high). A default here would stamp one onto every save.
   */
  printing: z
    .object({
      networkDriver: z.enum(['epsonSdk', 'rawEscPos']).optional(),
      drawerOpenSignal: z.enum(['high', 'low']).optional(),
    })
    .optional(),
  idleTimeoutSeconds: z.number().int().min(30).max(900).default(60),
  /** Enable the gateway's offline-payment relay for this restaurant (relayed to the gateway). */
  paymentRelayEnabled: z.boolean().default(false),
  /**
   * Enable the gateway's LAN/direct order-awareness watcher for this restaurant
   * (relayed to the gateway). Present on IPosConfig — declared here so a structured
   * posConfig save is not silently stripped of it by the schema.
   */
  awarenessRelayEnabled: z.boolean().default(false),
  /**
   * Gateway lease-authority mode for this LOCATION ('solo' | 'elected'),
   * relayed to the gateway. Deliberately `.optional()` with NO `.default()`:
   * an absent value must mean "the cloud is not expressing an opinion", so a
   * restaurant that has never set it keeps whatever its gateways are running.
   * A default here would stamp 'solo' onto every posConfig save and could
   * demote a deliberately-elected multi-gateway site to two always-primary
   * coordinators.
   */
  coordinatorMode: z.enum(['solo', 'elected']).optional(),
  /**
   * Whether this location accepts partial card approvals. Only an explicit
   * `true` is ON; absent means OFF (owner decision 2026-10-14 —
   * `isPartialApprovalAccepted`). Deliberately `.optional()` with NO
   * `.default()`: a default would stamp a value onto every posConfig save and
   * turn an unset location into an explicit choice nobody made.
   */
  acceptPartialApprovals: z.boolean().optional(),
});

// ─── Inferred Types ─────────────────────────────────────────────────────────

export type KitchenSendTriggerType = z.infer<typeof KitchenSendTriggerSchema>;
export type OrderCompletionModeType = z.infer<typeof OrderCompletionModeSchema>;
export type UpdateScheduleModeType = z.infer<typeof UpdateScheduleModeSchema>;
export type PosKitchenConfigType = z.infer<typeof PosKitchenConfigSchema>;
export type PosOrderCompletionConfigType = z.infer<typeof PosOrderCompletionConfigSchema>;
export type PosPaymentConfigType = z.infer<typeof PosPaymentConfigSchema>;
export type PosUpdateConfigType = z.infer<typeof PosUpdateConfigSchema>;
export type PosPrintingConfigType = NonNullable<z.infer<typeof PosConfigSchema>['printing']>;
export type PosConfigType = z.infer<typeof PosConfigSchema>;
